/**
 * Le passage d'une version a l'autre.
 *
 * LE PROBLEME QUE CE MODULE RESOUT
 *
 * Une page ne peut pas melanger deux versions de l'application. Elle a charge
 * son JavaScript de la version A ; si le worker de la version B prend la main,
 * tout fichier de A qu'elle demandera ensuite a disparu — et une application a
 * 82 routes chargees paresseusement en demande a chaque navigation.
 *
 * Le navigateur protege ca : le nouveau worker s'installe, puis **attend**. Il
 * ne prend la main que quand plus aucune page de l'ancienne version n'est
 * ouverte. C'est sur, mais sur un onglet garde ouvert toute la semaine, ca peut
 * ne jamais arriver.
 *
 * Ce module tient les deux bouts : le worker attend, et on propose a
 * l'utilisateur de faire le pas — en rechargeant, ce qui est la moitie
 * indispensable du `skipWaiting`.
 *
 * Voir le ticket 018, etage 2.
 */

/**
 * Y avait-il deja un worker aux commandes au chargement de la page ?
 *
 * A la toute premiere visite, non : le worker s'installe, s'active, et
 * `clientsClaim()` le fait prendre la page — ce qui declenche un
 * `controllerchange` qui n'est **pas** une mise a jour. Sans cette distinction,
 * chaque premiere visite se rechargerait toute seule.
 */
const avaitUnControleur = typeof navigator !== "undefined"
    && "serviceWorker" in navigator
    && navigator.serviceWorker.controller !== null;

/** Le worker installe qui attend son tour, quand il y en a un. */
let enAttente: ServiceWorker | null = null;

/** Un rechargement est deja engage : ne pas en declencher un second. */
let rechargementLance = false;

type Abonne = (disponible: boolean) => void;
const abonnes = new Set<Abonne>();

function annoncer() {
    abonnes.forEach(abonne => abonne(enAttente !== null));
}

function noterEnAttente(worker: ServiceWorker) {
    if (enAttente === worker) {
        return;
    }
    enAttente = worker;
    annoncer();
}

/**
 * Suivre la disponibilite d'une mise a jour.
 *
 * L'abonne est appele immediatement avec l'etat courant, puis a chaque
 * changement. Rend la fonction de desabonnement.
 */
export function sAbonnerALaMiseAJour(abonne: Abonne): () => void {
    abonnes.add(abonne);
    abonne(enAttente !== null);
    return () => {
        abonnes.delete(abonne);
    };
}

/** Une nouvelle version attend-elle ? Lecture ponctuelle, non reactive. */
export function miseAJourDisponible(): boolean {
    return enAttente !== null;
}

/**
 * Franchir le pas.
 *
 * `SKIP_WAITING` fait passer le worker en attente au premier plan ; le
 * `clientsClaim()` du worker le fait alors prendre cette page, ce qui declenche
 * le `controllerchange` ecoute plus bas, qui recharge.
 *
 * Le delai est un filet : si l'evenement ne venait pas — worker mort, message
 * perdu — on rechargerait quand meme. Recharger sans avoir bascule est sans
 * danger : on retombe simplement sur l'ancienne version, et le bandeau revient.
 */
export function appliquerLaMiseAJour() {
    if (!enAttente || rechargementLance) {
        return;
    }
    rechargementLance = true;
    enAttente.postMessage({type: "SKIP_WAITING"});
    window.setTimeout(() => window.location.reload(), 3000);
}

/** Vingt minutes entre deux verifications de fond. */
const INTERVALLE_VERIFICATION = 20 * 60 * 1000;

/** Et cinq minutes au minimum entre deux verifications, quelle qu'en soit la cause. */
const DELAI_MINIMUM = 5 * 60 * 1000;

let derniereVerification = 0;

/**
 * Installer le suivi.
 *
 * Appele une fois, au demarrage. Il fait trois choses :
 *
 * - recharger quand le worker change, c'est-a-dire quand la bascule a eu lieu.
 *   Y compris dans les **autres onglets** : celui qui n'a pas clique se
 *   retrouverait sinon servi par le nouveau worker tout en executant l'ancien
 *   code, ce qui est exactement la situation qu'on cherche a eviter ;
 * - reperer un worker en attente, qu'il vienne d'arriver ou qu'il attende deja
 *   depuis une session precedente ;
 * - **demander au navigateur de verifier**. C'est le point le moins evident :
 *   un navigateur ne verifie le script du worker qu'a une navigation, ou au
 *   plus une fois par jour. Or une application a page unique ne navigue jamais
 *   vraiment. Sans ces verifications explicites, un deploiement pourrait rester
 *   invisible pendant vingt-quatre heures.
 */
export function installerLeSuiviDesMisesAJour() {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
        return;
    }

    navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (!avaitUnControleur || rechargementLance) {
            return;
        }
        rechargementLance = true;
        window.location.reload();
    });

    navigator.serviceWorker.ready.then(registration => {
        if (registration.waiting) {
            noterEnAttente(registration.waiting);
        }

        registration.addEventListener("updatefound", () => {
            const arrivant = registration.installing;
            if (!arrivant) {
                return;
            }
            arrivant.addEventListener("statechange", () => {
                // Sans controleur, ce n'est pas une mise a jour mais la toute
                // premiere installation : il n'y a rien a proposer.
                if (arrivant.state === "installed" && navigator.serviceWorker.controller) {
                    noterEnAttente(arrivant);
                }
            });
        });

        const verifier = () => {
            const maintenant = Date.now();
            if (maintenant - derniereVerification < DELAI_MINIMUM) {
                return;
            }
            derniereVerification = maintenant;
            registration.update().catch(() => {
                // Hors ligne, ou serveur indisponible. On reessaiera.
            });
        };

        window.setInterval(verifier, INTERVALLE_VERIFICATION);

        // Revenir sur l'onglet est le moment ou l'on est le plus dispose a
        // accepter une mise a jour, et le plus probable d'en avoir manque une.
        document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "visible") {
                verifier();
            }
        });
    }).catch(() => {
        // Pas de worker actif : rien a suivre.
    });
}
